import React, { useEffect, useMemo, useState } from 'react';
import { API_BASE_URL, getAuthHeaders } from '../utils/api';
import { MAX_TOOL_INGESTION_GROUPS } from '../utils/toolGroupKeys';
import './IngestionGroupMultiPicker.css';

async function fetchGroupsForSuffix(suffix) {
  const url = suffix
    ? `${API_BASE_URL}/casc/v1/api/data-review/groups?suffix=${encodeURIComponent(suffix)}&all=true`
    : `${API_BASE_URL}/casc/v1/api/data-review/groups?all=true`;
  const headers = await getAuthHeaders();
  const resp = await fetch(url, { method: 'GET', headers });
  if (!resp.ok) throw new Error('Failed to load ingestion groups');
  const data = await resp.json();
  const list = Array.isArray(data?.groups) ? data.groups : [];
  return list.map((g) => ({
    slug: g.group_slug || g.id || '',
    name: g.name || g.group_slug || '',
  })).filter((g) => g.slug);
}

/**
 * Multi-select ingestion groups via popup (1–5), avoiding long dropdown lists.
 */
function IngestionGroupMultiPicker({
  suffix,
  selectedSlugs = [],
  onChange,
  disabled = false,
  error = null,
}) {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [filter, setFilter] = useState('');

  const selected = useMemo(
    () => (Array.isArray(selectedSlugs) ? selectedSlugs : []).map((s) => String(s || '').trim()).filter(Boolean),
    [selectedSlugs]
  );

  const atMax = selected.length >= MAX_TOOL_INGESTION_GROUPS;

  useEffect(() => {
    let active = true;
    setLoading(true);
    fetchGroupsForSuffix(suffix)
      .then((list) => { if (active) setGroups(list); })
      .catch(() => { if (active) setGroups([]); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [suffix]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    if (!q) return groups;
    return groups.filter(
      (g) => g.slug.toLowerCase().includes(q) || (g.name || '').toLowerCase().includes(q)
    );
  }, [groups, filter]);

  const addGroup = (slug) => {
    const s = String(slug || '').trim();
    if (!s || selected.includes(s) || atMax) return;
    onChange([...selected, s]);
    setFilter('');
  };

  const removeGroup = (slug) => {
    onChange(selected.filter((x) => x !== slug));
  };

  const labelFor = (slug) => {
    const g = groups.find((x) => x.slug === slug);
    return g?.name ? `${g.name} (${slug})` : slug;
  };

  return (
    <div className="ingestion-group-multi-picker">
      <div className="ingestion-group-chips">
        {selected.length === 0 && (
          <span className="ingestion-group-placeholder">No ingestion groups selected</span>
        )}
        {selected.map((slug) => (
          <span key={slug} className="ingestion-group-chip">
            <span className="ingestion-group-chip-label" title={slug}>{labelFor(slug)}</span>
            {!disabled && (
              <button
                type="button"
                className="ingestion-group-chip-remove"
                onClick={() => removeGroup(slug)}
                aria-label={`Remove ${slug}`}
              >
                ×
              </button>
            )}
          </span>
        ))}
      </div>
      <div className="ingestion-group-actions">
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          onClick={() => setPickerOpen(true)}
          disabled={disabled || atMax}
        >
          <i className="fas fa-plus" /> Add ingestion group
        </button>
        <small className="form-help">
          {selected.length}/{MAX_TOOL_INGESTION_GROUPS} groups — tool searches any selected group
        </small>
      </div>
      {error && (
        <div className="field-error">
          <i className="fas fa-exclamation-triangle" /> {error}
        </div>
      )}

      {pickerOpen && (
        <div className="ingestion-group-picker-overlay">
          <div
            className="ingestion-group-picker-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="ingestion-group-picker-title"
          >
            <div className="ingestion-group-picker-header">
              <h3 id="ingestion-group-picker-title">Add ingestion group</h3>
              <button type="button" className="close" onClick={() => setPickerOpen(false)} aria-label="Close">
                ×
              </button>
            </div>
            <input
              type="search"
              className="form-control ingestion-group-picker-search"
              placeholder="Filter by name or slug…"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              autoFocus
            />
            <div className="ingestion-group-picker-list">
              {loading && <div className="ingestion-group-picker-status">Loading groups…</div>}
              {!loading && filtered.length === 0 && (
                <div className="ingestion-group-picker-status">No groups match your filter.</div>
              )}
              {!loading && filtered.map((g) => {
                const isSelected = selected.includes(g.slug);
                return (
                  <button
                    key={g.slug}
                    type="button"
                    className={`ingestion-group-picker-row${isSelected ? ' is-selected' : ''}`}
                    disabled={isSelected || atMax}
                    onClick={() => {
                      addGroup(g.slug);
                      if (selected.length + 1 >= MAX_TOOL_INGESTION_GROUPS) setPickerOpen(false);
                    }}
                  >
                    <span className="ingestion-group-picker-row-name">{g.name || g.slug}</span>
                    <span className="ingestion-group-picker-row-slug">{g.slug}</span>
                    {isSelected && <span className="ingestion-group-picker-badge">Added</span>}
                  </button>
                );
              })}
            </div>
            <div className="ingestion-group-picker-footer">
              <button type="button" className="btn btn-primary" onClick={() => setPickerOpen(false)}>
                Done
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default IngestionGroupMultiPicker;
